import { isFuture, isPast, isToday, parseISO } from 'date-fns';
import { Booking } from '@/types';
import { Demand } from '@/types/demand';

/**
 * Canonical, comparison-safe form of a booking status. Statuses reach the UI as
 * free-text from the API, so casing/whitespace and the American spelling of
 * "cancelled" must not change how a booking is classified.
 */
export function normaliseBookingStatus(status: string | null | undefined): string {
  const value = String(status ?? '').trim().toLowerCase();
  return value === 'canceled' ? 'cancelled' : value;
}

function isUnresourced(booking: Booking): boolean {
  return (
    booking.resourceId === 'unallocated' ||
    booking.resourceId === null ||
    booking.resourceId === undefined ||
    (booking.resourceIds?.length === 1 && booking.resourceIds[0] === 'unallocated')
  );
}

function endsInThePast(booking: Booking): boolean {
  const end = parseISO(booking.endTime);
  return isPast(end) && !isToday(end);
}

function endsTodayOrLater(booking: Booking): boolean {
  const end = parseISO(booking.endTime);
  return isFuture(end) || isToday(end);
}

export interface ProjectBookingBuckets {
  completed: Booking[];
  planned: Booking[];
  unresourced: Booking[];
}

/**
 * Splits a project's bookings into the buckets shown on the Bookings tab.
 * Cancelled bookings are dropped by the caller; a booking's own status always
 * wins over its dates, so a completed booking never shows as planned.
 */
export function classifyProjectBookings(bookings: Booking[]): ProjectBookingBuckets {
  const completed: Booking[] = [];
  const planned: Booking[] = [];
  const unresourced: Booking[] = [];

  bookings.forEach((booking) => {
    const status = normaliseBookingStatus(booking.status);
    if (status === 'completed' || endsInThePast(booking)) {
      completed.push(booking);
      return;
    }
    if (!endsTodayOrLater(booking)) return;
    if (isUnresourced(booking)) unresourced.push(booking);
    else planned.push(booking);
  });

  return { completed, planned, unresourced };
}

/** A resource request that still has hours nobody is booked against. */
export interface OpenDemandEntry {
  id: number;
  roleName: string;
  remainingHours: number;
  requestedStartDate?: string | null;
}

/**
 * Outstanding demand for a project. These are resource requests the project
 * still needs covering, so they belong in the Unresourced bucket alongside
 * unallocated bookings.
 */
export function openDemandEntries(demands: Demand[]): OpenDemandEntry[] {
  return demands
    .filter((demand) => normaliseBookingStatus(demand.status) !== 'cancelled')
    .map((demand) => ({
      id: demand.id,
      roleName: demand.roleName,
      remainingHours: Math.max(0, (demand.totalHours ?? 0) - (demand.allocatedHours ?? 0)),
      requestedStartDate: demand.requestedStartDate,
    }))
    .filter((entry) => entry.remainingHours > 0);
}
